/**
 * Central API URL helper: most routes use same-origin `/api` on the app host (Vercel + Express).
 * Routes in EDGE_ROUTES use Supabase Edge. Saved cards (`reserve/payment-methods`) use Edge so Stripe
 * is called from Deno (Supabase) — avoids Vercel serverless → Stripe connection failures.
 * Use `mergeSupabaseEdgeHeaders()` for Edge fetches (gateway `apikey` + JWT).
 *
 * Edge base is derived from `VITE_SUPABASE_URL` so Defera Zero/Capital Edge calls target the right project.
 */
function getSupabaseEdgeBase() {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (url && typeof url === 'string') {
    return `${url.replace(/\/$/, '')}/functions/v1`;
  }
  // Fallback only if env failed to inline (broken build). Prefer fixing VITE_SUPABASE_URL in Vercel.
  return 'https://dnqlxvcolycsrjzgntqk.supabase.co/functions/v1';
}

const SUPABASE_EDGE_BASE = getSupabaseEdgeBase();

/**
 * Routes served by Supabase Edge (cross-origin). Each function must allow needed headers in CORS.
 */
const EDGE_ROUTES = new Set([
  'defera-zero/create-initial-setup-intent',
  'defera-zero/setup-intent',
  'defera-zero/update-payment-method',
  'defera-zero/create-subscription',
  'defera-zero/schedule',
  'defera-zero/preview-early-payment',
  'defera-zero/charge-early-payment',
  'defera-zero/preview-schedule',
  'defera-zero/update-schedule',
  'defera-zero/cancel-trip',
  // The 75% cash refund path — preview (no body flag) then confirm.
  'defera-zero/cancel-refund',
  'defera-capital/create-initial-setup-intent',
  'defera-capital/setup-intent',
  'defera-capital/update-payment-method',
  'defera-capital/create-subscription',
  'defera-capital/checkout-status',
  'defera-capital/schedule',
  'defera-capital/preview-early-payment',
  'defera-capital/charge-early-payment',
  'defera-capital/preview-skip-payment',
  'defera-capital/skip-payment',
  'defera-capital/preview-schedule',
  'defera-capital/update-schedule',
  'defera-capital/cancel-trip',
  'defera-capital/restart-plan',
  // NMI / Vector Payments track for Defera Capital. Active when
  // platform_settings.defera_capital_processor === 'nmi'. Functions live
  // in supabase/functions/defera-capital-nmi-*.
  'defera-capital-nmi/create-tokenization-session',
  'defera-capital-nmi/create-update-card-session',
  'defera-capital-nmi/update-customer-vault',
  'defera-capital-nmi/create-checkout',
  'defera-capital-nmi/checkout-status',
  'defera-capital-nmi/schedule',
  'defera-capital-nmi/preview-early-payment',
  'defera-capital-nmi/charge-early-payment',
  'defera-capital-nmi/preview-skip-payment',
  'defera-capital-nmi/skip-payment',
  'defera-capital-nmi/preview-schedule',
  'defera-capital-nmi/update-schedule',
  'defera-capital-nmi/cancel-trip',
  'defera-capital-nmi/restart-plan',
  // Defera Credit (house credit) ledger — customer balance read + admin grant
  // + customer-initiated application to a Confirmed trip's upcoming payments
  // + defaulted-unbooked trip → credit conversion (customer opt-in).
  'defera-credit/balance',
  'defera-credit/admin',
  'defera-credit/apply',
  'defera-credit/default-opt-in',
  'reserve/payment-methods',
  // Stripe-touching Reserve endpoints: Vercel serverless → Stripe Node SDK is flaky and hits
  // StripeConnectionError ("Request was retried 5 times"). Edge uses fetch() to api.stripe.com and
  // is reliable, so route every Stripe write here. Matching edge functions live in
  // supabase/functions/reserve-*. If you add/remove one, keep this list in sync.
  'reserve/create-payment-intent',
  'reserve/confirm-payment',
  'reserve/create-setup-intent',
  'reserve/ensure-customer',
  'reserve/set-default-payment-method',
  'reserve/detach-payment-method',
  'admin/radar/risk-insights',
  // Admin-only: reinstate a defaulted trip (regenerates the payment schedule
  // for the remaining balance + re-hooks Stripe/NMI so installments resume).
  'admin/reinstate-defaulted-trip',
  'bookings/change-flight/preview',
  'bookings/change-flight',
  'bookings/change-flight/confirm',
  // Add Faye travel insurance to a booked trip from Trip Details (quote + apply).
  'bookings/trip-insurance',
  // Customer Activity feed — aggregates per-user booking events across NMI
  // installments, manual payments, flight changes, cancellations, schedule
  // changes, card updates, credits, and miles. Read-only; mirrors how
  // defera-capital-nmi/schedule is invoked (Bearer JWT + apikey).
  'customer/activity-feed',
  // Admin-only upcoming-NMI-payments view used by the /activity page's
  // "Upcoming" tab. Read-only aggregator over nmi_installment_attempts that
  // groups every open installment by booking + enriches with card + user
  // email, so we can verify the daily cron is firing without inspecting the
  // table by hand.
  'customer/upcoming-nmi-payments',
]);

/** Routes that map to differently-named Edge Functions (path -> fn name) */
const EDGE_FN_OVERRIDES = {
  'reserve/payment-methods': 'reserve-payment-methods',
  'admin/radar/risk-insights': 'radar-risk-insights',
  'admin/reinstate-defaulted-trip': 'admin-reinstate-defaulted-trip',
  'bookings/change-flight/preview': 'change-flight-preview',
  'bookings/change-flight': 'change-flight',
  'bookings/change-flight/confirm': 'change-flight-confirm',
  'bookings/trip-insurance': 'trip-insurance-add',
};

/**
 * @param {string} path - e.g. 'reserve/create-setup-intent' or '/api/reserve/create-setup-intent'
 * @returns {string} Full URL (Edge Function or /api path)
 */
export function getApiUrl(path) {
  const stripped = path.replace(/^\/api\//, '');
  const qIndex = stripped.indexOf('?');
  const normalized = qIndex >= 0 ? stripped.slice(0, qIndex) : stripped;
  // Preserve any query string when rewriting to an Edge Function URL. Without
  // this, `getApiUrl('defera-credit/admin?user_id=…')` silently dropped the
  // query and the function received no params (admin credit view showed $0).
  const query = qIndex >= 0 ? stripped.slice(qIndex) : '';
  if (EDGE_ROUTES.has(normalized)) {
    const fnName = EDGE_FN_OVERRIDES[normalized] || normalized.replace(/\//g, '-');
    return `${SUPABASE_EDGE_BASE}/${fnName}${query}`;
  }
  return path.startsWith('/') ? path : `/api/${path}`;
}

/** Convenience: reserve path */
export function reserveUrl(path) {
  return getApiUrl(`reserve/${path}`);
}

/** Convenience: defera-zero path */
export function deferaZeroUrl(path) {
  return getApiUrl(`defera-zero/${path}`);
}

/** Defera Capital: Supabase Edge (same routing model as defera-zero), not Vercel /api */
export function deferaCapitalUrl(path) {
  return getApiUrl(`defera-capital/${path.replace(/^\//, '')}`);
}

/** Defera Capital — NMI track. Active when the processor toggle is `nmi`. */
export function deferaCapitalNmiUrl(path) {
  return getApiUrl(`defera-capital-nmi/${path.replace(/^\//, '')}`);
}

/** Defera Credit (house credit) ledger — balance read + admin grant. */
export function deferaCreditUrl(path) {
  return getApiUrl(`defera-credit/${path.replace(/^\//, '')}`);
}

/** Customer Activity feed (read-only aggregator). */
export function customerActivityFeedUrl() {
  return getApiUrl('customer/activity-feed');
}

/** Admin: upcoming NMI installments grouped by booking (read-only). Used by
 *  the /activity page's "Upcoming" tab to verify the daily cron is firing. */
export function customerUpcomingNmiPaymentsUrl() {
  return getApiUrl('customer/upcoming-nmi-payments');
}
